import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { type Control, useFormContext, useWatch } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import { InfoBanner } from '@/shared/components/InfoBanner';
import type { Renter } from '@/utils/types/renter.interface';

import { hasSmbRequiredPricingParams } from '../utils';

const infoBannerText = {
  textAlign: 'left',
  fontSize: '16px',
  lineHeight: '24px',
  [theme.breakpoints.down('md')]: { fontSize: '14px', lineHeight: '22px' },
} as const;

type InviteSmbStateCoverageInfoBannerProps = {
  /** Legacy drawer passes control explicitly; shared form reads from FormProvider. */
  control?: Control<Renter>;
};

function InviteSmbStateCoverageInfoBannerView({ control }: { control: Control<Renter> }) {
  const leaseStartDate = useWatch({ control, name: 'leaseStartDate' });
  const leaseEndDate = useWatch({ control, name: 'leaseEndDate' });
  const grossMonthlyRent = useWatch({ control, name: 'grossMonthlyRent' });

  if (hasSmbRequiredPricingParams({ leaseStartDate, leaseEndDate, grossMonthlyRent })) {
    return null;
  }

  return (
    <InfoBanner
      dataTestId="invite-smb-state-coverage-info-banner"
      icon={<LockOutlinedIcon fontSize="inherit" />}
      sx={{
        mb: 2,
        alignItems: 'flex-start',
        '& .MuiTypography-root': infoBannerText,
      }}
    >
      {`Please complete the lease information above first. We use this to calculate your coverage options.`}
    </InfoBanner>
  );
}

export function InviteSmbStateCoverageInfoBanner({
  control: controlProp,
}: InviteSmbStateCoverageInfoBannerProps) {
  const formContext = useFormContext<Renter>();
  const control = controlProp ?? formContext?.control;

  if (!control) {
    return null;
  }

  return <InviteSmbStateCoverageInfoBannerView control={control} />;
}
